import { CbhIcon, Rating, Title } from "@clipboard-health/ui-components";
import { Box, DialogContent, Fade, Stack } from "@mui/material";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { TextButton } from "@src/appV2/redesign/components/TextButton";
import { useState } from "react";
import { useFormContext } from "react-hook-form";

import {
  DEFAULT_QUESTION_TAG_ICON,
  type PostShiftReviewFormValues,
  QUESTION_TAG_ICON_MAP,
  QuestionTag,
  type StepAction,
} from "../types";

export interface RatingQuestionStepProps {
  questionId: string;
  questionText: string;
  tag: QuestionTag;
  isSubmitting?: boolean;
  onAction: (action: StepAction) => void;
}

export function RatingQuestionStep({
  questionId,
  questionText,
  tag,
  isSubmitting = false,
  onAction,
}: RatingQuestionStepProps) {
  const { watch } = useFormContext<PostShiftReviewFormValues>();
  const ratingAnswers = watch("ratingAnswers") ?? {};
  const existingRating = ratingAnswers[questionId];
  const [selectedRating, setSelectedRating] = useState<number>(
    existingRating ? Number(existingRating) : 0
  );

  const hasRating = selectedRating > 0;
  const iconType =
    (
      QUESTION_TAG_ICON_MAP as Partial<
        Record<QuestionTag, (typeof QUESTION_TAG_ICON_MAP)[keyof typeof QUESTION_TAG_ICON_MAP]>
      >
    )[tag] ?? DEFAULT_QUESTION_TAG_ICON;

  return (
    <>
      <DialogContent sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Stack spacing={6} alignItems="center" sx={{ px: 0 }}>
          <CbhIcon type={iconType} size="large" />

          <Title variant="h3" component="h3" textAlign="center">
            {questionText}
          </Title>

          <Box sx={{ pt: 1 }}>
            <Rating
              value={selectedRating}
              precision={1}
              size="large"
              disabled={isSubmitting}
              onChange={(_event, newValue) => {
                if (newValue !== null) {
                  setSelectedRating(newValue);
                }
              }}
            />
          </Box>
        </Stack>
      </DialogContent>

      <DialogFooter orientation="vertical">
        {/* Fixed height to prevent layout shift when toggling between Skip and Continue */}
        <Stack spacing={3} sx={{ width: "100%", height: "100px" }}>
          {hasRating && (
            <Fade in appear>
              <Box>
                <LoadingButton
                  fullWidth
                  variant="contained"
                  size="large"
                  isLoading={isSubmitting}
                  onClick={() => {
                    const ratingValue = String(selectedRating);
                    onAction({
                      type: "NEXT_STEP",
                      data: { questionId, ratingValue },
                    });
                  }}
                >
                  Continue
                </LoadingButton>
              </Box>
            </Fade>
          )}
          {!hasRating && !isSubmitting && (
            <Fade in appear>
              <Box sx={{ alignSelf: "center" }}>
                <TextButton
                  onClick={() => {
                    onAction({ type: "SKIP_STEP" });
                  }}
                >
                  Skip
                </TextButton>
              </Box>
            </Fade>
          )}
        </Stack>
      </DialogFooter>
    </>
  );
}
